import * as React from 'react';
import { CircularProgress, Icon, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import axios from "axios";
import Autocomplete from '@material-ui/lab/Autocomplete';
import strings from '../utils/StringManager';
import LogCompanyTooltip from './LogCompanyTooltip';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
    },
    autocomnplete: {
        flex: '1',
    },
    icon: {
        color: '#888',
        marginLeft: '7px',
    },
}));

interface IOption {
    name: string,
    id: number,
}

interface IProps {
    value: string,
    onChange: (value: string) => void,
    className?: string,
    applicationId: number,
    freeSolo?: boolean,

    errorText: string,
    isDemo: boolean,
    isLogSystemIntegrationAvailable: boolean,
}

export default function LogEnvironmentAutocomplete(props: IProps) {
    const classes = useStyles();

    const [selected, setSelected] = React.useState<string | IOption>(null);
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [loadedId, setLoadedId] = React.useState<number>();
    const [options, setOptions] = React.useState<IOption[]>([]);

    React.useEffect(() => {
        if (props.value)
            setSelected(props.value);
        else
            setSelected(null);
    }, [props.value]);

    React.useEffect(() => {
        let active = true;

        if (!open) {
            return;
        }

        if (loadedId === props.applicationId) {
            return;
        }

        setLoading(true);
        setLoadedId(props.applicationId);
        (async () => {
            const response = await axios.get<{ items: IOption[] }>("/LogDictionaries/Environments/?applicationId=" + props.applicationId);
           
            if (active) {
                setLoading(false);
                setOptions(response.data?.items ?? []);
            }
        })();

        return () => {
            active = false;
        };
    }, [props.applicationId, open]);

    function isVisible(option: IOption, search: string): boolean
    {
        if (option.name.toLowerCase().indexOf(search) >= 0)
            return true;
        
        return false;
    }

    function getString(option: IOption | string): string {
        if (typeof option === 'string') {
            return option;
        }
        else {
            return option.name;
        }
    }

    return (<div className={clsx(classes.root, props.className)}>
        <Autocomplete
            className={clsx("mi-lab-autocomplete-outlined", classes.autocomnplete)}
            
            value={selected}
            onChange={(event, newValue) => {
                setSelected(newValue);

                if (!newValue)
                    props.onChange("");
                else
                    props.onChange(getString(newValue));
            }}
            freeSolo={props.freeSolo}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            options={options}
            loading={loading}

            filterOptions={(options: IOption[], state: any) => {
                let search: string = state.inputValue;
                if (!search)
                    return options;
                search = search.toLocaleLowerCase();
                return options.filter((x) => isVisible(x, search));      
            }}
            getOptionSelected={(option, value) => {
                let optionString = getString(option);
                let valueString = getString(value);

                return optionString.toLowerCase() === valueString.toLowerCase();
            }}
            getOptionLabel={option => getString(option)}
            clearText={strings.get("clear")}
            noOptionsText={strings.get("noOptionsMessage")}
            openText={strings.get("open")}
            closeText={strings.get("close")}
            loadingText={strings.get("loading") + "..."}

            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    onChange={(event) => {
                        if (props.freeSolo) {
                            props.onChange(event.target.value);
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'disabled',
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            renderOption={option => {
                let opt = option as IOption;
                return <Typography noWrap>{opt.name}</Typography>
            }}
        />
    </div>);
}